import { db } from "@/firebaseConfig";
import {
  doc,
  collection,
  updateDoc,
  increment,
  getDoc,
  setDoc,
  arrayUnion,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getCountFromServer,
  getDocs,
  addDoc,
  Timestamp,
  startAfter,
  DocumentSnapshot,
} from "firebase/firestore";

// Debug mode for analytics - set to false to disable detailed logging
const ANALYTICS_DEBUG = false;

const debugLog = (..._args: any[]) => {
  if (ANALYTICS_DEBUG) {
  }
};

const debugError = (...args: any[]) => {
  if (ANALYTICS_DEBUG) {
    console.error(...args);
  }
};

// Analytics Events
export enum AnalyticsEventType {
  VIEW_ARTICLE = "view_article",
  VIEW_THREAD = "view_thread",
  LIKE_ARTICLE = "like_article",
  LIKE_POST = "like_post",
  CREATE_ARTICLE = "create_article",
  CREATE_THREAD = "create_thread",
  CREATE_POST = "create_post",
  CREATE_COMMENT = "create_comment",
  SHARE_CONTENT = "share_content",
  USER_LOGIN = "user_login",
  USER_JOIN = "user_join",
  USER_PROFILE_VIEW = "user_profile_view",
  GROUP_JOIN = "group_join",
  SEARCH_PERFORMED = "search_performed",
}

/**
 * Fix for a Firestore timestamp in the event data
 */
interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

/**
 * Analytics event interface
 */
export interface AnalyticsEvent {
  eventType: AnalyticsEventType;
  userId: string;
  timestamp: Date | FirestoreTimestamp;
  contentId?: string;
  groupId?: string;
  metadata?: Record<string, any>;
}

// User analytics summary
export interface UserAnalytics {
  userId: string;
  totalViews: number;
  totalLikes: number;
  totalPosts: number;
  totalArticles: number;
  totalComments: number;
  lastActive: Date;
  topViewedContent: Array<{
    contentId: string;
    contentType: string;
    views: number;
  }>;
}

// Content analytics summary
export interface ContentAnalytics {
  contentId: string;
  contentType: string; // "article", "thread", "post"
  views: number;
  likes: number;
  comments: number;
  shares: number;
  groupId?: string;
  authorId: string;
  lastViewed?: Date;
  weeklyViewsData?: Record<string, number>; // ISO week string to count
  viewsByRegion?: Record<string, number>; // Region to count
  viewsByDevice?: Record<string, number>; // Device type to count
}

// Analytics interfaces
export interface UserActivity {
  id?: string;
  userId: string;
  action: string;
  timestamp: Timestamp;
  metadata?: Record<string, any>;
  sessionId?: string;
  ipAddress?: string;
  userAgent?: string;
}

export interface SupportStaffActivity {
  id?: string;
  staffId: string;
  action: string;
  timestamp: Timestamp;
  ticketId?: string;
  duration?: number; // in seconds
  metadata?: Record<string, any>;
}

export interface AnalyticsReport {
  id?: string;
  type: "daily" | "weekly" | "monthly";
  period: string; // e.g., "2024-01-01" for daily, "2024-W01" for weekly
  generatedAt: Timestamp;
  data: {
    userActivity: {
      totalActions: number;
      uniqueUsers: number;
      topActions: Array<{ action: string; count: number }>;
    };
    supportActivity: {
      totalTicketsHandled: number;
      averageResponseTime: number;
      staffProductivity: Array<{
        staffId: string;
        ticketsHandled: number;
        averageResponseTime: number;
      }>;
    };
    systemMetrics: {
      pageViews: number;
      sessionDuration: number;
      errorRate: number;
    };
  };
}

const ANALYTICS_COLLECTION = "user_analytics";
const SUPPORT_ANALYTICS_COLLECTION = "support_staff_analytics";
const ANALYTICS_REPORTS_COLLECTION = "analytics_reports";

/**
 * Track an analytics event
 */
export const trackEvent = async (event: AnalyticsEvent): Promise<string> => {
  try {
    debugLog(
      "🔍 Starting analytics tracking for:",
      event.eventType,
      "user:",
      event.userId,
      "groupId:",
      event.groupId
    );

    // Add the event to the events collection
    const eventsRef = collection(db, "analytics_events");
    const newEvent = {
      ...event,
      timestamp: event.timestamp || new Date(),
    };

    debugLog("📝 Creating analytics event document...");
    // Create the document with auto-generated ID
    const eventDoc = doc(eventsRef);
    await setDoc(eventDoc, newEvent);
    debugLog("✅ Analytics event document created successfully");

    debugLog("📊 Starting summary metrics update...");
    // Update relevant summary metrics
    await updateSummaryMetrics(event);
    debugLog("✅ Summary metrics updated successfully");

    return eventDoc.id;
  } catch (error) {
    debugError("❌ Analytics tracking failed at:", error);
    debugError("Event details:", event);
    throw error;
  }
};

/**
 * Update the relevant summary metrics based on the event
 */
const updateSummaryMetrics = async (event: AnalyticsEvent) => {
  const { eventType, userId, contentId, groupId, metadata } = event;

  debugLog("📊 Updating summary metrics for:", {
    eventType,
    userId,
    contentId,
    groupId,
  });

  try {
    debugLog("👤 Updating user analytics...");
    // Update user analytics
    await updateUserAnalytics(eventType, userId);
    debugLog("✅ User analytics updated successfully");
  } catch (error) {
    debugError("❌ User analytics update failed:", error);
    throw error;
  }

  // Update content analytics if applicable
  if (contentId) {
    try {
      debugLog("📄 Updating content analytics for:", contentId);
      await updateContentAnalytics(eventType, contentId, userId, metadata);
      debugLog("✅ Content analytics updated successfully");
    } catch (error) {
      debugError("❌ Content analytics update failed:", error);
      throw error;
    }
  }

  // Track per-user group activity if applicable (powers the active-member count)
  if (groupId) {
    try {
      debugLog("👥 Tracking group activity for:", groupId);
      await trackGroupUserActivity(groupId, userId, eventType);
      debugLog("✅ Group activity tracked successfully");
    } catch (error) {
      debugError("❌ Group activity tracking failed:", error);
      throw error;
    }
  }
};

/**
 * Update user analytics summary
 */
const updateUserAnalytics = async (
  eventType: AnalyticsEventType,
  userId: string
) => {
  const userAnalyticsRef = doc(db, "analytics_users", userId);

  try {
    // Check if user analytics document exists
    const userAnalyticsDoc = await getDoc(userAnalyticsRef);

    if (!userAnalyticsDoc.exists()) {
      // Create new user analytics document if it doesn't exist
      await setDoc(userAnalyticsRef, {
        userId,
        totalViews: 0,
        totalLikes: 0,
        totalPosts: 0,
        totalArticles: 0,
        totalComments: 0,
        lastActive: new Date(),
        topViewedContent: [],
      });
    }

    // Update metrics based on event type
    const updates: any = {
      lastActive: new Date(),
    };

    switch (eventType) {
      case AnalyticsEventType.CREATE_POST:
        updates.totalPosts = increment(1);
        break;
      case AnalyticsEventType.CREATE_ARTICLE:
        updates.totalArticles = increment(1);
        break;
      case AnalyticsEventType.CREATE_COMMENT:
        updates.totalComments = increment(1);
        break;
      case AnalyticsEventType.LIKE_ARTICLE:
      case AnalyticsEventType.LIKE_POST:
        updates.totalLikes = increment(1);
        break;
    }

    await updateDoc(userAnalyticsRef, updates);
  } catch (error) {
    throw error;
  }
};

/**
 * Update content analytics
 */
const updateContentAnalytics = async (
  eventType: AnalyticsEventType,
  contentId: string,
  userId: string,
  metadata?: Record<string, any>
) => {
  const contentType = determineContentType(eventType);
  if (!contentType) return;

  const contentAnalyticsRef = doc(db, "analytics_content", contentId);

  try {
    // Check if content analytics document exists
    const contentDoc = await getDoc(contentAnalyticsRef);

    if (!contentDoc.exists()) {
      // Create new content analytics document
      await setDoc(contentAnalyticsRef, {
        contentId,
        contentType,
        views: 0,
        likes: 0,
        comments: 0,
        shares: 0,
        authorId: metadata?.authorId || "",
        groupId: metadata?.groupId || null,
        lastViewed: null,
        weeklyViewsData: {},
        viewsByRegion: {},
        viewsByDevice: {},
      });
    }

    // Get the ISO week string for current date (e.g., "2023-W32")
    const now = new Date();
    const weekString = getISOWeek(now);

    // Prepare updates based on event type
    const updates: any = {};

    switch (eventType) {
      case AnalyticsEventType.VIEW_ARTICLE:
      case AnalyticsEventType.VIEW_THREAD:
        updates.views = increment(1);
        updates.lastViewed = now;
        updates[`weeklyViewsData.${weekString}`] = increment(1);

        // Track device type if available
        if (metadata?.deviceType) {
          updates[`viewsByDevice.${metadata.deviceType}`] = increment(1);
        }

        // Track region if available
        if (metadata?.region) {
          updates[`viewsByRegion.${metadata.region}`] = increment(1);
        }

        // Update unique viewers array with view counts
        if (userId) {
          const uniqueViewersRef = doc(
            db,
            "analytics_content_viewers",
            contentId
          );
          const uniqueViewersDoc = await getDoc(uniqueViewersRef);

          if (!uniqueViewersDoc.exists()) {
            await setDoc(uniqueViewersRef, {
              contentId,
              viewerIds: [userId],
              viewDates: { [userId]: now },
              viewCounts: { [userId]: 1 },
              lastViewed: { [userId]: now },
            });
          } else {
            const viewerIds = uniqueViewersDoc.data().viewerIds || [];
            const viewCounts = uniqueViewersDoc.data().viewCounts || {};

            if (!viewerIds.includes(userId)) {
              // New viewer
              await updateDoc(uniqueViewersRef, {
                viewerIds: arrayUnion(userId),
                [`viewDates.${userId}`]: now,
                [`viewCounts.${userId}`]: 1,
                [`lastViewed.${userId}`]: now,
              });
            } else {
              // Returning viewer - increment view count
              const currentCount = viewCounts[userId] || 0;
              await updateDoc(uniqueViewersRef, {
                [`viewDates.${userId}`]: now,
                [`viewCounts.${userId}`]: currentCount + 1,
                [`lastViewed.${userId}`]: now,
              });
            }
          }
        }
        break;

      case AnalyticsEventType.LIKE_ARTICLE:
      case AnalyticsEventType.LIKE_POST:
        updates.likes = increment(1);
        break;

      case AnalyticsEventType.CREATE_COMMENT:
        updates.comments = increment(1);
        break;

      case AnalyticsEventType.SHARE_CONTENT:
        updates.shares = increment(1);
        break;
    }

    if (Object.keys(updates).length > 0) {
      await updateDoc(contentAnalyticsRef, updates);
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Track user activity in a group
 */
const trackGroupUserActivity = async (
  groupId: string,
  userId: string,
  eventType: AnalyticsEventType
) => {
  const activityRef = doc(
    db,
    "analytics_group_activity",
    `${groupId}_${userId}`
  );

  try {
    debugLog(
      "📋 Checking group activity document for:",
      `${groupId}_${userId}`
    );
    const activityDoc = await getDoc(activityRef);

    if (!activityDoc.exists()) {
      debugLog("📝 Creating new group activity document");
      // Create new activity document
      await setDoc(activityRef, {
        groupId,
        userId,
        activityCount: 1,
        lastActive: new Date(),
        lastEventType: eventType,
      });
      debugLog("✅ Group activity document created successfully");
    } else {
      debugLog("📊 Updating existing group activity document");
      // Update existing activity document
      await updateDoc(activityRef, {
        activityCount: increment(1),
        lastActive: new Date(),
        lastEventType: eventType,
      });
      debugLog("✅ Group activity document updated successfully");
    }
  } catch (error) {
    debugError("❌ Group user activity tracking failed:", error);
    // Allow this to continue failing for now since it's core user activity tracking
    throw error;
  }
};

/**
 * Count members of a group active in the last 7 days.
 * Uses a count aggregation so only the count is billed, not the activity docs.
 */
export const getGroupActiveMemberCount = async (
  groupId: string
): Promise<number> => {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  try {
    const countSnap = await getCountFromServer(
      query(
        collection(db, "analytics_group_activity"),
        where("groupId", "==", groupId),
        where("lastActive", ">=", oneWeekAgo)
      )
    );
    return countSnap.data().count;
  } catch (error) {
    debugError("Active member count failed (non-critical):", error);
    return 0;
  }
};

/**
 * Determine content type based on event type
 */
const determineContentType = (eventType: AnalyticsEventType): string | null => {
  switch (eventType) {
    case AnalyticsEventType.VIEW_ARTICLE:
    case AnalyticsEventType.LIKE_ARTICLE:
    case AnalyticsEventType.CREATE_ARTICLE:
      return "article";
    case AnalyticsEventType.VIEW_THREAD:
    case AnalyticsEventType.CREATE_THREAD:
      return "thread";
    case AnalyticsEventType.LIKE_POST:
    case AnalyticsEventType.CREATE_POST:
      return "post";
    default:
      return null;
  }
};

/**
 * Get the ISO week string (e.g., "2023-W32")
 */
const getISOWeek = (date: Date): string => {
  // Create new date object for the calculation
  const d = new Date(date);

  // Set to the nearest Thursday (current date + 4 - current day)
  // Make Sunday's day number 7
  const dayNum = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - dayNum);

  // Get the first day of the year
  const yearStart = new Date(d.getFullYear(), 0, 1);

  // Calculate the week number
  const weekDiff = Math.floor((d.getTime() - yearStart.getTime()) / 86400000);
  const weekNum = Math.ceil((weekDiff + 1) / 7);

  // Format with leading zero if needed
  const weekStr = weekNum < 10 ? `0${weekNum}` : `${weekNum}`;

  return `${d.getFullYear()}-W${weekStr}`;
};

/**
 * Get analytics for a specific user
 */
export const getUserAnalytics = async (
  userId: string
): Promise<UserAnalytics | null> => {
  try {
    const userAnalyticsRef = doc(db, "analytics_users", userId);
    const userDoc = await getDoc(userAnalyticsRef);

    if (userDoc.exists()) {
      return userDoc.data() as UserAnalytics;
    }

    return null;
  } catch (error) {
    throw error;
  }
};

/**
 * Get analytics for a specific content item
 */
export const getContentAnalytics = async (
  contentId: string
): Promise<ContentAnalytics | null> => {
  try {
    const contentAnalyticsRef = doc(db, "analytics_content", contentId);
    const contentDoc = await getDoc(contentAnalyticsRef);

    if (contentDoc.exists()) {
      return contentDoc.data() as ContentAnalytics;
    }

    return null;
  } catch (error) {
    throw error;
  }
};

/**
 * Get view counts for multiple content items
 */
export const getContentViewCounts = async (
  contentIds: string[]
): Promise<Record<string, number>> => {
  try {
    if (contentIds.length === 0) {
      return {};
    }

    // Batch fetch analytics for all content IDs
    const promises = contentIds.map(async (contentId) => {
      try {
        const analytics = await getContentAnalytics(contentId);
        return { contentId, views: analytics?.views || 0 };
      } catch (error) {
        console.error(`Error fetching analytics for ${contentId}:`, error);
        return { contentId, views: 0 };
      }
    });

    const results = await Promise.all(promises);

    // Convert to a record for easy lookup
    const viewCounts: Record<string, number> = {};
    results.forEach(({ contentId, views }) => {
      viewCounts[contentId] = views;
    });

    return viewCounts;
  } catch (error) {
    console.error("Error fetching content view counts:", error);
    return {};
  }
};

/**
 * Get analytics for a specific group
 */
/**
 * Get trending content across the platform
 */
export const getTrendingContent = async (
  limit: number = 10,
  contentType?: string
): Promise<ContentAnalytics[]> => {
  try {
    const contentRef = collection(db, "analytics_content");
    let contentQuery;

    if (contentType) {
      contentQuery = query(
        contentRef,
        where("contentType", "==", contentType),
        orderBy("views", "desc"),
        firestoreLimit(limit)
      );
    } else {
      contentQuery = query(
        contentRef,
        orderBy("views", "desc"),
        firestoreLimit(limit)
      );
    }

    try {
      const querySnapshot = await getDocs(contentQuery);
      return querySnapshot.docs.map((doc) => doc.data() as ContentAnalytics);
    } catch (error) {
      // If we get a permission error, return an empty array instead of throwing
      if (
        error instanceof Error &&
        error.toString().includes("Missing or insufficient permissions")
      ) {
        return [];
      }
      // Rethrow other errors
      throw error;
    }
  } catch (error) {
    return []; // Return empty array instead of throwing
  }
};

/**
 * Track an article view
 */
export const trackArticleView = async (
  articleId: string,
  userId: string,
  groupId?: string,
  metadata?: Record<string, any>
): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.VIEW_ARTICLE,
    userId,
    contentId: articleId,
    groupId,
    timestamp: new Date(),
    metadata,
  });
};

/**
 * Track a thread view - SAFE VERSION with error handling
 */
export const trackThreadViewSafe = async (
  threadId: string,
  userId: string,
  groupId?: string,
  metadata?: Record<string, any>
): Promise<{ success: boolean; error?: string }> => {
  try {
    await trackEvent({
      eventType: AnalyticsEventType.VIEW_THREAD,
      userId,
      contentId: threadId,
      groupId,
      timestamp: new Date(),
      metadata,
    });
    return { success: true };
  } catch (error) {
    debugError("Analytics tracking failed (non-blocking):", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Track a thread view
 */
export const trackThreadView = async (
  threadId: string,
  userId: string,
  groupId?: string,
  metadata?: Record<string, any>
): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.VIEW_THREAD,
    userId,
    contentId: threadId,
    groupId,
    timestamp: new Date(),
    metadata,
  });
};

/**
 * Track a content like
 */
export const trackContentLike = async (
  contentId: string,
  contentType: "article" | "post",
  userId: string,
  groupId?: string
): Promise<void> => {
  const eventType =
    contentType === "article"
      ? AnalyticsEventType.LIKE_ARTICLE
      : AnalyticsEventType.LIKE_POST;

  await trackEvent({
    eventType,
    userId,
    contentId,
    groupId,
    timestamp: new Date(),
  });
};

/**
 * Track content creation
 */
export const trackContentCreation = async (
  contentType: "article" | "thread" | "post" | "comment",
  contentId: string,
  userId: string,
  groupId?: string
): Promise<void> => {
  let eventType: AnalyticsEventType;

  switch (contentType) {
    case "article":
      eventType = AnalyticsEventType.CREATE_ARTICLE;
      break;
    case "thread":
      eventType = AnalyticsEventType.CREATE_THREAD;
      break;
    case "post":
      eventType = AnalyticsEventType.CREATE_POST;
      break;
    case "comment":
      eventType = AnalyticsEventType.CREATE_COMMENT;
      break;
    default:
      return;
  }

  await trackEvent({
    eventType,
    userId,
    contentId,
    groupId,
    timestamp: new Date(),
  });
};

/**
 * Track user login
 */
export const trackUserLogin = async (userId: string): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.USER_LOGIN,
    userId,
    timestamp: new Date(),
  });
};

/**
 * Track user join
 */
export const trackUserJoin = async (userId: string): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.USER_JOIN,
    userId,
    timestamp: new Date(),
  });
};

/**
 * Track content sharing
 */
export const trackContentShare = async (
  contentId: string,
  userId: string,
  shareTarget: string, // e.g., 'twitter', 'facebook', 'email'
  groupId?: string
): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.SHARE_CONTENT,
    userId,
    contentId,
    groupId,
    timestamp: new Date(),
    metadata: {
      shareTarget,
    },
  });
};

/**
 * Track user profile view
 */
export const trackProfileView = async (
  profileId: string,
  viewerId: string
): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.USER_PROFILE_VIEW,
    userId: viewerId,
    contentId: profileId, // Using contentId for the profile being viewed
    timestamp: new Date(),
  });
};

/**
 * Track group join
 */
export const trackGroupJoin = async (
  groupId: string,
  userId: string
): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.GROUP_JOIN,
    userId,
    groupId,
    timestamp: new Date(),
  });
};

/**
 * Track search performed
 */
export const trackSearch = async (
  userId: string,
  searchQuery: string,
  resultsCount: number
): Promise<void> => {
  await trackEvent({
    eventType: AnalyticsEventType.SEARCH_PERFORMED,
    userId,
    timestamp: new Date(),
    metadata: {
      searchQuery,
      resultsCount,
    },
  });
};

/**
 * Track general user activity
 */
export const trackUserActivity = async (
  userId: string,
  action: string,
  metadata?: Record<string, any>
): Promise<void> => {
  try {
    const activity: Omit<UserActivity, "id"> = {
      userId,
      action,
      timestamp: Timestamp.now(),
      metadata: metadata || {},
      sessionId: getSessionId(),
    };

    await addDoc(collection(db, ANALYTICS_COLLECTION), activity);

    // Also store in localStorage for immediate access
    const localActivities = JSON.parse(
      localStorage.getItem("userActivities") || "[]"
    );
    localActivities.push({
      ...activity,
      timestamp: new Date().toISOString(),
    });

    // Keep only last 50 activities in localStorage
    if (localActivities.length > 50) {
      localActivities.splice(0, localActivities.length - 50);
    }

    localStorage.setItem("userActivities", JSON.stringify(localActivities));
  } catch (error) {
    debugError("Error tracking user activity:", error);
  }
};

/**
 * Track support staff specific activity
 */
export const trackSupportStaffActivity = async (
  staffId: string,
  action: string,
  ticketId?: string,
  duration?: number,
  metadata?: Record<string, any>
): Promise<void> => {
  try {
    const activity: Omit<SupportStaffActivity, "id"> = {
      staffId,
      action,
      timestamp: Timestamp.now(),
      metadata: metadata || {},
    };

    // Only include optional fields if they have values
    if (ticketId !== undefined) {
      activity.ticketId = ticketId;
    }
    if (duration !== undefined) {
      activity.duration = duration;
    }

    await addDoc(collection(db, SUPPORT_ANALYTICS_COLLECTION), activity);

    // Also store in localStorage for immediate access
    const localStaffActivities = JSON.parse(
      localStorage.getItem("supportStaffActivity") || "[]"
    );
    localStaffActivities.push({
      ...activity,
      timestamp: new Date().toISOString(),
    });

    // Keep only last 100 activities in localStorage
    if (localStaffActivities.length > 100) {
      localStaffActivities.splice(0, localStaffActivities.length - 100);
    }

    localStorage.setItem(
      "supportStaffActivity",
      JSON.stringify(localStaffActivities)
    );
  } catch (error) {
    debugError("Error tracking support staff activity:", error);
  }
};

/**
 * Get user activities with pagination
 */
export const getUserActivities = async (
  userId: string,
  pageSize: number = 50,
  lastDoc: DocumentSnapshot | null = null
): Promise<{
  activities: UserActivity[];
  lastDoc: DocumentSnapshot | null;
}> => {
  try {
    let q;
    if (lastDoc) {
      q = query(
        collection(db, ANALYTICS_COLLECTION),
        where("userId", "==", userId),
        orderBy("timestamp", "desc"),
        startAfter(lastDoc),
        firestoreLimit(pageSize)
      );
    } else {
      q = query(
        collection(db, ANALYTICS_COLLECTION),
        where("userId", "==", userId),
        orderBy("timestamp", "desc"),
        firestoreLimit(pageSize)
      );
    }

    const querySnapshot = await getDocs(q);
    const activities: UserActivity[] = [];

    querySnapshot.forEach((doc) => {
      activities.push({ id: doc.id, ...doc.data() } as UserActivity);
    });

    const lastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;

    return { activities, lastDoc: lastVisible };
  } catch (error) {
    debugError("Error getting user activities:", error);
    return { activities: [], lastDoc: null };
  }
};

/**
 * Get support staff activities
 */
export const getSupportStaffActivities = async (
  staffId?: string,
  pageSize: number = 50,
  lastDoc: DocumentSnapshot | null = null
): Promise<{
  activities: SupportStaffActivity[];
  lastDoc: DocumentSnapshot | null;
}> => {
  try {
    let q;
    const constraints = [];

    if (staffId) {
      constraints.push(where("staffId", "==", staffId));
    }

    constraints.push(orderBy("timestamp", "desc"));

    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    constraints.push(firestoreLimit(pageSize));

    q = query(collection(db, SUPPORT_ANALYTICS_COLLECTION), ...constraints);

    const querySnapshot = await getDocs(q);
    const activities: SupportStaffActivity[] = [];

    querySnapshot.forEach((doc) => {
      activities.push({ id: doc.id, ...doc.data() } as SupportStaffActivity);
    });

    const lastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;

    return { activities, lastDoc: lastVisible };
  } catch (error) {
    debugError("Error getting support staff activities:", error);
    return { activities: [], lastDoc: null };
  }
};

/**
 * Generate analytics report
 */
export const generateAnalyticsReport = async (
  type: "daily" | "weekly" | "monthly",
  period: string
): Promise<AnalyticsReport | null> => {
  try {
    // Check if report already exists
    const existingReportQuery = query(
      collection(db, ANALYTICS_REPORTS_COLLECTION),
      where("type", "==", type),
      where("period", "==", period)
    );

    const existingReports = await getDocs(existingReportQuery);
    if (!existingReports.empty) {
      const existingReport = existingReports.docs[0];
      return {
        id: existingReport.id,
        ...existingReport.data(),
      } as AnalyticsReport;
    }

    // Calculate date range for the period
    const { startDate, endDate } = getPeriodDateRange(type, period);

    // Get user activities for the period
    const userActivitiesQuery = query(
      collection(db, ANALYTICS_COLLECTION),
      where("timestamp", ">=", Timestamp.fromDate(startDate)),
      where("timestamp", "<=", Timestamp.fromDate(endDate))
    );

    const userActivitiesSnapshot = await getDocs(userActivitiesQuery);
    const userActivities: UserActivity[] = [];
    userActivitiesSnapshot.forEach((doc) => {
      userActivities.push({ id: doc.id, ...doc.data() } as UserActivity);
    });

    // Get support staff activities for the period
    const supportActivitiesQuery = query(
      collection(db, SUPPORT_ANALYTICS_COLLECTION),
      where("timestamp", ">=", Timestamp.fromDate(startDate)),
      where("timestamp", "<=", Timestamp.fromDate(endDate))
    );

    const supportActivitiesSnapshot = await getDocs(supportActivitiesQuery);
    const supportActivities: SupportStaffActivity[] = [];
    supportActivitiesSnapshot.forEach((doc) => {
      supportActivities.push({
        id: doc.id,
        ...doc.data(),
      } as SupportStaffActivity);
    });

    // Process analytics data
    const uniqueUsers = new Set(userActivities.map((a) => a.userId)).size;
    const actionCounts = userActivities.reduce((acc, activity) => {
      acc[activity.action] = (acc[activity.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topActions = Object.entries(actionCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([action, count]) => ({ action, count }));

    // Process support staff productivity
    const staffProductivity = supportActivities.reduce((acc, activity) => {
      if (!acc[activity.staffId]) {
        acc[activity.staffId] = {
          staffId: activity.staffId,
          ticketsHandled: 0,
          totalResponseTime: 0,
          responseCount: 0,
        };
      }

      if (activity.action === "ticket_handled") {
        acc[activity.staffId].ticketsHandled++;
      }

      if (activity.action === "ticket_responded" && activity.duration) {
        acc[activity.staffId].totalResponseTime += activity.duration;
        acc[activity.staffId].responseCount++;
      }

      return acc;
    }, {} as Record<string, any>);

    const staffProductivityArray = Object.values(staffProductivity).map(
      (staff: any) => ({
        staffId: staff.staffId,
        ticketsHandled: staff.ticketsHandled,
        averageResponseTime:
          staff.responseCount > 0
            ? Math.round(staff.totalResponseTime / staff.responseCount)
            : 0,
      })
    );

    const report: Omit<AnalyticsReport, "id"> = {
      type,
      period,
      generatedAt: Timestamp.now(),
      data: {
        userActivity: {
          totalActions: userActivities.length,
          uniqueUsers,
          topActions,
        },
        supportActivity: {
          totalTicketsHandled: supportActivities.filter(
            (a) => a.action === "ticket_handled"
          ).length,
          averageResponseTime: calculateAverageResponseTime(supportActivities),
          staffProductivity: staffProductivityArray,
        },
        systemMetrics: {
          pageViews: userActivities.filter((a) => a.action.includes("view"))
            .length,
          sessionDuration: calculateAverageSessionDuration(userActivities),
          errorRate: calculateErrorRate(userActivities),
        },
      },
    };

    // Save the report
    const reportDoc = await addDoc(
      collection(db, ANALYTICS_REPORTS_COLLECTION),
      report
    );
    return { id: reportDoc.id, ...report };
  } catch (error) {
    debugError("Error generating analytics report:", error);
    return null;
  }
};

/**
 * Get analytics reports
 */
export const getAnalyticsReports = async (
  type?: "daily" | "weekly" | "monthly",
  limit_count: number = 10
): Promise<AnalyticsReport[]> => {
  try {
    let q;
    if (type) {
      q = query(
        collection(db, ANALYTICS_REPORTS_COLLECTION),
        where("type", "==", type),
        orderBy("generatedAt", "desc"),
        firestoreLimit(limit_count)
      );
    } else {
      q = query(
        collection(db, ANALYTICS_REPORTS_COLLECTION),
        orderBy("generatedAt", "desc"),
        firestoreLimit(limit_count)
      );
    }

    const querySnapshot = await getDocs(q);
    const reports: AnalyticsReport[] = [];

    querySnapshot.forEach((doc) => {
      reports.push({ id: doc.id, ...doc.data() } as AnalyticsReport);
    });

    return reports;
  } catch (error) {
    debugError("Error getting analytics reports:", error);
    return [];
  }
};

// Helper functions
function getSessionId(): string {
  let sessionId = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    sessionStorage.setItem("sessionId", sessionId);
  }
  return sessionId;
}

function getPeriodDateRange(
  type: "daily" | "weekly" | "monthly",
  period: string
) {
  const today = new Date();
  let startDate: Date, endDate: Date;

  switch (type) {
    case "daily":
      startDate = new Date(period);
      endDate = new Date(period);
      endDate.setDate(endDate.getDate() + 1);
      break;
    case "weekly":
      // period format: "2024-W01"
      const [year, week] = period.split("-W");
      startDate = new Date(parseInt(year), 0, 1 + (parseInt(week) - 1) * 7);
      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      break;
    case "monthly":
      // period format: "2024-01"
      const [monthYear, month] = period.split("-");
      startDate = new Date(parseInt(monthYear), parseInt(month) - 1, 1);
      endDate = new Date(parseInt(monthYear), parseInt(month), 1);
      break;
    default:
      startDate = today;
      endDate = today;
  }

  return { startDate, endDate };
}

function calculateAverageResponseTime(
  activities: SupportStaffActivity[]
): number {
  const responseTimes = activities
    .filter((a) => a.action === "ticket_responded" && a.duration)
    .map((a) => a.duration!);

  if (responseTimes.length === 0) return 0;

  const sum = responseTimes.reduce((acc, time) => acc + time, 0);
  return Math.round(sum / responseTimes.length);
}

function calculateAverageSessionDuration(activities: UserActivity[]): number {
  // Group activities by session
  const sessions = activities.reduce((acc, activity) => {
    if (!activity.sessionId) return acc;

    if (!acc[activity.sessionId]) {
      acc[activity.sessionId] = [];
    }
    acc[activity.sessionId].push(activity);
    return acc;
  }, {} as Record<string, UserActivity[]>);

  const sessionDurations = Object.values(sessions).map((sessionActivities) => {
    if (sessionActivities.length < 2) return 0;

    const sortedActivities = sessionActivities.sort(
      (a, b) => a.timestamp.toMillis() - b.timestamp.toMillis()
    );

    const start = sortedActivities[0].timestamp.toMillis();
    const end =
      sortedActivities[sortedActivities.length - 1].timestamp.toMillis();

    return (end - start) / 1000; // Convert to seconds
  });

  if (sessionDurations.length === 0) return 0;

  const sum = sessionDurations.reduce((acc, duration) => acc + duration, 0);
  return Math.round(sum / sessionDurations.length);
}

function calculateErrorRate(activities: UserActivity[]): number {
  const totalActions = activities.length;
  const errorActions = activities.filter(
    (a) => a.action.includes("error") || a.action.includes("failed")
  ).length;

  if (totalActions === 0) return 0;

  return Math.round((errorActions / totalActions) * 100 * 100) / 100; // Round to 2 decimal places
}

/**
 * Track content like - SAFE VERSION with error handling
 */
export const trackContentLikeSafe = async (
  contentId: string,
  contentType: "article" | "post",
  userId: string,
  groupId?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await trackContentLike(contentId, contentType, userId, groupId);
    return { success: true };
  } catch (error) {
    debugError("Content like analytics tracking failed (non-blocking):", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Track content creation - SAFE VERSION with error handling
 */
export const trackContentCreationSafe = async (
  contentType: "article" | "thread" | "post" | "comment",
  contentId: string,
  userId: string,
  groupId?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await trackContentCreation(contentType, contentId, userId, groupId);
    return { success: true };
  } catch (error) {
    debugError(
      "Content creation analytics tracking failed (non-blocking):",
      error
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Get detailed viewer analytics for content (admins and authors only)
 */
export const getContentViewerDetails = async (
  contentId: string,
  requesterId: string,
  authorId?: string
): Promise<{
  success: boolean;
  viewers?: Array<{
    userId: string;
    viewCount: number;
    firstViewed: Date;
    lastViewed: Date;
    userProfile?: {
      firstName?: string;
      lastName?: string;
      handle?: string;
      profilePicture?: string;
    };
  }>;
  totalViews?: number;
  uniqueViewers?: number;
  error?: string;
}> => {
  try {
    // Check if requester is authorized (must be author or admin)
    const { getUserById } = await import("./users.service");
    const requesterData = await getUserById(requesterId);

    const isAuthor = authorId === requesterId;
    const isStaff = requesterData?.isStaff === true;

    if (!isAuthor && !isStaff) {
      return {
        success: false,
        error: "Only authors and administrators can view detailed analytics",
      };
    }

    // Get viewer details
    const viewersRef = doc(db, "analytics_content_viewers", contentId);
    const viewersDoc = await getDoc(viewersRef);

    if (!viewersDoc.exists()) {
      return {
        success: true,
        viewers: [],
        totalViews: 0,
        uniqueViewers: 0,
      };
    }

    const viewersData = viewersDoc.data();
    const viewerIds = viewersData.viewerIds || [];
    const viewCounts = viewersData.viewCounts || {};
    const viewDates = viewersData.viewDates || {};
    const lastViewed = viewersData.lastViewed || {};

    // Fetch user profiles for viewers
    const viewers = await Promise.all(
      viewerIds.map(async (userId: string) => {
        const userProfile = await getUserById(userId);
        return {
          userId,
          viewCount: viewCounts[userId] || 1,
          firstViewed: viewDates[userId]
            ? new Date(viewDates[userId].seconds * 1000)
            : new Date(),
          lastViewed: lastViewed[userId]
            ? new Date(lastViewed[userId].seconds * 1000)
            : new Date(),
          userProfile: userProfile
            ? {
                firstName: userProfile.firstName,
                lastName: userProfile.lastName,
                handle: userProfile.handle,
                profilePicture: userProfile.profilePicture,
              }
            : undefined,
        };
      })
    );

    // Sort by view count (most views first)
    viewers.sort((a, b) => b.viewCount - a.viewCount);

    const totalViews = Object.values(viewCounts).reduce(
      (sum: number, count: any) => sum + (count || 1),
      0
    );

    return {
      success: true,
      viewers,
      totalViews,
      uniqueViewers: viewerIds.length,
    };
  } catch (error) {
    debugError("Error getting content viewer details:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to get viewer details",
    };
  }
};
